import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  deleteSupportMessageAPI,
  fetchChatMessagesAPI,
  fetchChatsAPI,
  markMessagesAsReadByAdminAPI,
  sendSupportMessageAPI,
  updateChatAccessAPI,
  updateSupportMessageAPI,
} from "./supportChatAPI";

const CHATS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 30;

const initialState = {
  chats: [],
  // The list's latest request: its state, and which page it asked for (1 =
  // the list itself or a new search, more = scrolling down). Only the latest
  // request's answer is applied, so a slow old search can't overwrite a
  // newer one.
  fetchAllChatsStatus: "idle",
  fetchAllChatsError: null,
  chatsRequestPage: null,
  chatsRequestId: null,
  // The search the list was last asked for, and the one `chats` shows.
  chatsQuery: "",
  chatsListQuery: "",
  chatsPagination: {
    page: 1,
    limit: CHATS_PAGE_SIZE,
    hasMore: true,
  },
  // Counts messages for chats the list doesn't hold; the open list then
  // refreshes its first page (ChatList).
  chatsMissedActivity: 0,

  // chatId → { items, status, olderStatus, hasMore, error }
  //   status: the newest page ("idle" | "loading" | "succeeded" | "failed")
  //   olderStatus: scrolling back ("idle" | "loading" | "failed")
  // A message sent from here is shown at once with a clientId and a delivery
  // status ("sending" | "failed") until the server's copy replaces it; that
  // copy keeps the clientId as localKey, so it stays the same row on screen.
  messages: {},
  markReadInFlight: {},

  activeChatId: null,
  // The open chat's player (the chat's populated userId).
  activeUser: null,
  chatAccessPendingUserId: null,

  // Which list the open tab shows: "all" (شات المستخدمين) or "prime"
  // (شات البرايم — Prime players' chats only).
  chatsPlan: "all",
};

export const fetchSupportChats = createAsyncThunk(
  "supportChat/fetchSupportChats",
  async ({ page = 1, username = "" } = {}, { getState, rejectWithValue }) => {
    const { chatsPlan: plan, chatsPagination } = getState().supportChat;
    try {
      const res = await fetchChatsAPI({
        page,
        limit: chatsPagination.limit,
        username,
        plan,
      });
      return { chats: res.data.chats, hasMore: res.data.hasMore, page, username };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch support chats",
      );
    }
  },
);

// The first page again, merged in quietly: a player the list didn't hold just
// wrote. Only for the plain list; a search keeps its results.
export const refreshSupportChatsHead = createAsyncThunk(
  "supportChat/refreshSupportChatsHead",
  async (_, { getState, rejectWithValue }) => {
    const { chatsPlan: plan, chatsPagination } = getState().supportChat;
    try {
      const res = await fetchChatsAPI({ page: 1, limit: chatsPagination.limit, plan });
      return { chats: res.data.chats, plan };
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState().supportChat;
      return !state.chatsQuery && state.fetchAllChatsStatus !== "loading";
    },
  },
);

// beforeId: the oldest message shown, to load the page before it; none = the
// newest page.
export const fetchSupportMessages = createAsyncThunk(
  "supportChat/fetchMessages",
  async ({ chatId, beforeId = null }, { rejectWithValue }) => {
    try {
      const res = await fetchChatMessagesAPI(chatId, {
        beforeId,
        limit: MESSAGES_PAGE_SIZE,
      });
      return { messages: res.data.messages, hasMore: res.data.hasMore };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch messages",
      );
    }
  },
);

// clientId: made by the sender, so the message shows at once and a retry
// reuses its place.
export const sendSupportMessage = createAsyncThunk(
  "supportChat/sendMessage",
  async ({ chatId, message }, { rejectWithValue }) => {
    try {
      const res = await sendSupportMessageAPI(chatId, message);
      return res.data.message;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send message",
      );
    }
  },
);

// A reply from the composer: shown in the thread at once, sent behind it.
export const queueSupportMessage = ({ chatId, text }) =>
  sendSupportMessage({ chatId, message: text, clientId: `local-${crypto.randomUUID()}` });

export const editSupportMessage = createAsyncThunk(
  "supportChat/editMessage",
  async ({ chatId, messageId, message }, { rejectWithValue }) => {
    try {
      const res = await updateSupportMessageAPI(chatId, messageId, message);
      return {
        chatId,
        message: res.data.message,
        chatSummary: res.data.chatSummary,
      };
    } catch (error) {
      return rejectWithValue({
        chatId,
        error:
          error.response?.data?.message || "Failed to update support message",
      });
    }
  },
);

export const deleteSupportMessage = createAsyncThunk(
  "supportChat/deleteMessage",
  async ({ chatId, messageId }, { rejectWithValue }) => {
    try {
      const res = await deleteSupportMessageAPI(chatId, messageId);
      return {
        chatId,
        message: res.data.message,
        chatSummary: res.data.chatSummary,
      };
    } catch (error) {
      return rejectWithValue({
        chatId,
        error:
          error.response?.data?.message || "Failed to delete support message",
      });
    }
  },
);

// One request per chat at a time: the thread asks again whenever it's at the
// bottom with unread messages.
export const markAsReadByAdmin = createAsyncThunk(
  "supportChat/markAsReadByAdmin",
  async (chatId, { rejectWithValue }) => {
    try {
      await markMessagesAsReadByAdminAPI(chatId);
      return chatId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark messages as read",
      );
    }
  },
  {
    condition: (chatId, { getState }) =>
      Boolean(chatId) && !getState().supportChat.markReadInFlight[chatId],
  },
);

export const toggleChatAccess = createAsyncThunk(
  "users/toggleChatAccess",
  async ({ userId, access }, { dispatch, rejectWithValue }) => {
    try {
      const updatedUser = await updateChatAccessAPI(userId, access);
      dispatch(
        showModal({
          message: `تم ${access ? "تفعيل" : "تعطيل"} المحادثة لهذا المستخدم`,
          status: "success",
        }),
      );

      return updatedUser?.data?.user;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ أثناء تحديث حالة المحادثة",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to update chat access",
      );
    }
  },
);

const threadOf = (state, chatId) => {
  if (!state.messages[chatId]) {
    state.messages[chatId] = {
      items: [],
      status: "idle",
      olderStatus: "idle",
      hasMore: true,
      error: null,
    };
  }
  return state.messages[chatId];
};

// A new message moves its chat to the top of the list with a fresh preview.
// lastSenderId: the player's id, or null for support (as the list reads it).
const bumpChat = (state, chatId, message) => {
  const index = state.chats.findIndex((chat) => chat._id === chatId);
  if (index === -1) return false;
  const fromPlayer = Boolean(message.senderId);
  const chat = {
    ...state.chats[index],
    lastMessage: message.message,
    lastMessageAt: message.createdAt,
    lastSenderId: fromPlayer ? message.senderId._id || message.senderId : null,
    unreadByAdminCount: fromPlayer
      ? (state.chats[index].unreadByAdminCount || 0) + 1
      : state.chats[index].unreadByAdminCount,
  };
  state.chats.splice(index, 1);
  state.chats.unshift(chat);
  return true;
};

// An edit or delete: the message in its thread, the preview in the list.
const applyMessagePatch = (state, { chatId, message, chatSummary }) => {
  if (!message?._id) return;
  const items = chatId && state.messages[chatId]?.items;
  if (items) {
    const index = items.findIndex((item) => item._id === message._id);
    if (index !== -1) {
      const { localKey } = items[index];
      items[index] = localKey ? { ...message, localKey } : message;
    }
  }
  if (chatId && chatSummary) {
    const index = state.chats.findIndex((chat) => chat._id === chatId);
    if (index !== -1) {
      state.chats[index] = {
        ...state.chats[index],
        lastMessage: chatSummary.lastMessage,
        lastMessageAt: chatSummary.lastMessageAt,
        lastSenderId: chatSummary.lastSenderId,
      };
    }
  }
};

const supportChatSlice = createSlice({
  name: "supportChat",
  initialState,
  reducers: {
    // Switching between شات المستخدمين and شات البرايم: a fresh list and no
    // open chat (one from the other list may not belong in this one). A
    // request still on its way for the other list is dropped.
    setChatsPlan: (state, action) => {
      const plan = action.payload === "prime" ? "prime" : "all";
      if (state.chatsPlan === plan) return;
      state.chatsPlan = plan;
      state.chats = [];
      state.chatsQuery = "";
      state.chatsListQuery = "";
      state.chatsRequestId = null;
      state.chatsRequestPage = null;
      state.fetchAllChatsStatus = "idle";
      state.fetchAllChatsError = null;
      state.chatsPagination.page = 1;
      state.chatsPagination.hasMore = true;
      state.activeChatId = null;
      state.activeUser = null;
    },

    openSupportChat: (state, action) => {
      const chat = action.payload;
      state.activeChatId = chat?._id || null;
      state.activeUser = chat?.userId || null;
    },

    // supportChat:newMessage — the player's messages and every staff reply,
    // this tab's own included (the send may have answered first).
    addSocketMessage: (state, action) => {
      const { chatId, message } = action.payload || {};
      if (!chatId || !message?._id) return;

      // Only a thread already read gets it; one opened later reads it anyway.
      const thread = state.messages[chatId];
      if (thread?.status === "succeeded" && !thread.items.some((item) => item._id === message._id)) {
        const ownPending = message.senderId
          ? -1
          : thread.items.findIndex(
              (item) => item.status === "sending" && item.message === message.message,
            );
        if (ownPending !== -1) {
          thread.items[ownPending] = { ...message, localKey: thread.items[ownPending].clientId };
        } else {
          thread.items.push(message);
        }
      }

      if (!bumpChat(state, chatId, message)) state.chatsMissedActivity += 1;
    },

    applySupportMessagePatch: (state, action) => {
      applyMessagePatch(state, action.payload || {});
    },

    // A message that failed to send, dropped instead of retried.
    discardUnsentMessage: (state, action) => {
      const { chatId, clientId } = action.payload;
      const thread = state.messages[chatId];
      if (!thread) return;
      thread.items = thread.items.filter((item) => item.clientId !== clientId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportChats.pending, (state, action) => {
        state.fetchAllChatsStatus = "loading";
        state.fetchAllChatsError = null;
        state.chatsRequestId = action.meta.requestId;
        state.chatsRequestPage = action.meta.arg?.page || 1;
        state.chatsQuery = action.meta.arg?.username || "";
      })
      .addCase(fetchSupportChats.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.chatsRequestId) return;
        const { chats, hasMore, page, username } = action.payload;

        state.fetchAllChatsStatus = "succeeded";
        state.chatsRequestPage = null;
        state.chatsPagination.page = page;
        state.chatsPagination.hasMore = hasMore;

        if (page === 1) {
          state.chats = chats;
          state.chatsListQuery = username;
          state.chatsMissedActivity = 0;
        } else {
          const known = new Set(state.chats.map((chat) => chat._id));
          state.chats.push(...chats.filter((chat) => !known.has(chat._id)));
        }
      })
      .addCase(fetchSupportChats.rejected, (state, action) => {
        if (action.meta.requestId !== state.chatsRequestId) return;
        state.fetchAllChatsStatus = "failed";
        state.fetchAllChatsError = action.payload || "Failed to fetch support chats";
        // A failed search shows nothing, not the previous search's results.
        if (state.chatsRequestPage === 1 && state.chatsListQuery !== state.chatsQuery) {
          state.chats = [];
          state.chatsListQuery = state.chatsQuery;
          state.chatsPagination.hasMore = false;
        }
      })

      .addCase(refreshSupportChatsHead.fulfilled, (state, action) => {
        const { chats, plan } = action.payload;
        if (plan !== state.chatsPlan || state.chatsQuery || state.chatsListQuery) return;
        const fresh = new Set(chats.map((chat) => chat._id));
        state.chats = [...chats, ...state.chats.filter((chat) => !fresh.has(chat._id))];
        state.chatsMissedActivity = 0;
      })

      .addCase(fetchSupportMessages.pending, (state, action) => {
        const { chatId, beforeId } = action.meta.arg;
        const thread = threadOf(state, chatId);
        if (beforeId) {
          thread.olderStatus = "loading";
        } else {
          thread.status = "loading";
          thread.error = null;
        }
      })
      .addCase(fetchSupportMessages.fulfilled, (state, action) => {
        const { chatId, beforeId } = action.meta.arg;
        const { messages, hasMore } = action.payload;
        const thread = threadOf(state, chatId);

        if (beforeId) {
          thread.olderStatus = "idle";
          // The thread was reloaded meanwhile: this page no longer joins on.
          if (thread.items[0]?._id !== beforeId) return;
          const known = new Set(thread.items.map((item) => item._id));
          thread.items.unshift(...messages.filter((item) => !known.has(item._id)));
          thread.hasMore = hasMore;
          return;
        }

        // The newest page replaces what was shown, keeping what arrived live
        // after it was read and what is still being sent from here.
        const fetched = new Set(messages.map((item) => item._id));
        const newest = messages.length
          ? new Date(messages[messages.length - 1].createdAt).getTime()
          : 0;
        const later = thread.items.filter(
          (item) =>
            !item.clientId &&
            !fetched.has(item._id) &&
            new Date(item.createdAt).getTime() > newest,
        );
        const unsent = thread.items.filter((item) => item.clientId);
        thread.items = [...messages, ...later, ...unsent];
        thread.hasMore = hasMore;
        thread.status = "succeeded";
        thread.olderStatus = "idle";
      })
      .addCase(fetchSupportMessages.rejected, (state, action) => {
        const { chatId, beforeId } = action.meta.arg;
        const thread = threadOf(state, chatId);
        if (beforeId) {
          thread.olderStatus = "failed";
        } else {
          thread.status = "failed";
          thread.error = action.payload || "Failed to fetch messages";
        }
      })

      .addCase(sendSupportMessage.pending, (state, action) => {
        const { chatId, message, clientId } = action.meta.arg;
        const thread = threadOf(state, chatId);
        // A retry goes to the end, as a message sent now.
        thread.items = thread.items.filter((item) => item.clientId !== clientId);
        thread.items.push({
          _id: clientId,
          clientId,
          chatId,
          message,
          senderId: null,
          createdAt: new Date().toISOString(),
          status: "sending",
        });
      })
      .addCase(sendSupportMessage.fulfilled, (state, action) => {
        const { chatId, clientId } = action.meta.arg;
        const saved = action.payload;
        const thread = threadOf(state, chatId);
        const local = thread.items.findIndex((item) => item.clientId === clientId);
        const echoed = thread.items.some((item) => item._id === saved._id);
        if (local !== -1) {
          if (echoed) thread.items.splice(local, 1);
          else thread.items[local] = { ...saved, localKey: clientId };
        } else if (!echoed) {
          thread.items.push(saved);
        }
        bumpChat(state, chatId, saved);
      })
      .addCase(sendSupportMessage.rejected, (state, action) => {
        const { chatId, clientId } = action.meta.arg;
        const item = state.messages[chatId]?.items.find(
          (entry) => entry.clientId === clientId,
        );
        if (item) item.status = "failed";
      })

      .addCase(markAsReadByAdmin.pending, (state, action) => {
        state.markReadInFlight[action.meta.arg] = true;
      })
      .addCase(markAsReadByAdmin.fulfilled, (state, action) => {
        const chatId = action.payload;
        delete state.markReadInFlight[chatId];

        const thread = state.messages[chatId];
        if (thread) {
          thread.items = thread.items.map((item) =>
            item.senderId && !item.read ? { ...item, read: true } : item,
          );
        }

        const chat = state.chats.find((entry) => entry._id === chatId);
        if (chat) chat.unreadByAdminCount = 0;
      })
      .addCase(markAsReadByAdmin.rejected, (state, action) => {
        delete state.markReadInFlight[action.meta.arg];
      })

      .addCase(toggleChatAccess.pending, (state, action) => {
        state.chatAccessPendingUserId = action.meta.arg.userId;
      })
      .addCase(toggleChatAccess.fulfilled, (state, action) => {
        state.chatAccessPendingUserId = null;
        const updatedUser = action.payload;
        if (!updatedUser?._id) return;

        state.chats = state.chats.map((chat) =>
          chat.userId?._id === updatedUser._id
            ? { ...chat, userId: { ...chat.userId, chatAccess: updatedUser.chatAccess } }
            : chat,
        );
        if (state.activeUser?._id === updatedUser._id) {
          state.activeUser.chatAccess = updatedUser.chatAccess;
        }
      })
      .addCase(toggleChatAccess.rejected, (state) => {
        state.chatAccessPendingUserId = null;
      })

      .addCase(editSupportMessage.fulfilled, (state, action) => {
        applyMessagePatch(state, action.payload);
      })
      .addCase(deleteSupportMessage.fulfilled, (state, action) => {
        applyMessagePatch(state, action.payload);
      });
  },
});

export const {
  setChatsPlan,
  openSupportChat,
  addSocketMessage,
  applySupportMessagePatch,
  discardUnsentMessage,
} = supportChatSlice.actions;

export default supportChatSlice.reducer;
